import classNames from "classnames";
import { truncate } from "lodash";
import { observer } from "mobx-react";
import formatFilesize from "pretty-bytes";
import React, { useState } from "react";
import { CloserLoaderComponent } from "@fidget/common/components/loader/closerLoader";
import placeholder from "../../assets/photo_file_placeholder.png";
import { intl } from "../../i18n/TypedIntl";
import { L10n } from "../../i18n";
import { IAttachment } from "../../models/chat/chatItems";
import { FileUploadState, IFileUploadInfo, isUploadFailed } from "../../models/fileUpload";
import { ImageComponent } from "../image/image";
import { TooltipComponent } from "../tooltipComponent";
import styles from "./fileThumbnail.css";
import { fileService } from "../../services/files/fileService";
import { isIosBrowser, isIosWebview } from "../../utils/browserSupport";
import { getWheelhouseDownloadUrl } from "@fidget/common/utils/urlMappingHelpers";

type Props = {
  attachment?: IAttachment;
  fileUploadInfo?: IFileUploadInfo;
  small?: boolean;
  isInFirstRow?: boolean;
  isInScrollable?: boolean;
  onRetry?: (fileUpload: IFileUploadInfo) => void;
  onRemove?: (fileUpload: IFileUploadInfo) => void;
  widgetStyle?: boolean;
  onFileDownload?: (fileUrl: string, fileId?: string, fileName?: string) => void;
};

export const FileThumbnail = observer(
  ({
    onRetry,
    onRemove,
    small,
    isInFirstRow,
    isInScrollable,
    fileUploadInfo,
    attachment,
    widgetStyle,
    onFileDownload
  }: Props) => {
    const [temporaryLink, setTemporaryLink] = useState<string>();

    const getErrorOverlayMessage = (): string | undefined => {
      if (!fileUploadInfo) {
        return undefined;
      }
      switch (fileUploadInfo.uploadState) {
        case FileUploadState.VIRUS_FOUND:
          return intl.formatMessage("conversation.files.upload.virus");
        case FileUploadState.TOO_BIG:
          return intl.formatMessage("conversation.files.upload.tooBig");
        case FileUploadState.UNSUPPORTED_TYPE:
          return intl.formatMessage("conversation.files.upload.unsupportedType");
        case FileUploadState.MALFORMED_FILE_CONTENT:
          return intl.formatMessage("conversation.files.upload.malformedFileContent");
        default:
          return intl.formatMessage("conversation.files.upload.failed");
      }
    };

    const shouldDisplayErrorRetry = (): boolean => {
      if (!fileUploadInfo || !onRetry) {
        return false;
      }

      const { uploadState } = fileUploadInfo;
      if (
        uploadState === FileUploadState.TOO_BIG ||
        uploadState === FileUploadState.VIRUS_FOUND ||
        uploadState === FileUploadState.UNSUPPORTED_TYPE ||
        uploadState === FileUploadState.MALFORMED_FILE_CONTENT
      ) {
        return false;
      }

      return true;
    };

    const getShortFileName = (name: string) => {
      const length = widgetStyle ? (small ? 25 : 35) : small ? 16 : 24;
      return truncate(name, { length });
    };

    const handleRetry = (fileUpload: IFileUploadInfo) => {
      if (onRetry) {
        onRetry(fileUpload);
      }
    };

    const handleDelete = (fileUpload: IFileUploadInfo) => {
      if (onRemove) {
        onRemove(fileUpload);
      }
    };

    const renderImage = (url: string) => (
      <ImageComponent className={styles.content} src={url} fallbackSrc={placeholder} />
    );

    const renderFileInfo = (fileName?: string, fileSize?: number) => (
      <div className={styles.content}>
        <div className={styles["file-info"]}>
          <div className={classNames(styles.text, styles["file-name"])}>{fileName}</div>
          {fileSize && !widgetStyle && (
            <div className={classNames(styles.text, styles["file-size"])}>
              {formatFilesize(fileSize)}
            </div>
          )}
        </div>
      </div>
    );

    const renderFold = () => (
      <div className={classNames(styles["fold-foreground"], small && styles.small)} />
    );

    const renderRemoveFileButton = () => (
      <button
        onClick={() => fileUploadInfo && handleDelete(fileUploadInfo)}
        className={styles["remove-button"]}
        type={"button"}
      >
        <i className="icon-close-cross" />
      </button>
    );

    const renderErrorOverlay = (hasPreview: boolean) => {
      return (
        <div className={classNames(styles["error-overlay"], hasPreview && styles.photo)}>
          {!widgetStyle && (
            <div>
              <i className="icon-exclamation-mark" />
            </div>
          )}
          <div className={styles["upload-error"]}>{getErrorOverlayMessage()}</div>
          {shouldDisplayErrorRetry() && (
            <button
              onClick={() => fileUploadInfo && handleRetry(fileUploadInfo)}
              className={classNames(styles["upload-error"], styles["upload-error-button"])}
            >
              {intl.formatMessage("conversation.files.upload.retry")}
            </button>
          )}
        </div>
      );
    };

    const renderLocalFile = (uploadInfo: IFileUploadInfo) => {
      const { file, dataUrl, uploadState, isNotReady } = uploadInfo;
      const hasPreview = file.type.includes("image/");
      const shortFileName = getShortFileName(file.name);
      return (
        <TooltipComponent
          text={file.name}
          tooltipShow={!!file.name}
          positionPreset={!isInScrollable ? "top" : isInFirstRow ? "bottom" : "top"}
          breakWords
          style={widgetStyle ? "widget" : "dashboard"}
          width={widgetStyle ? "calc(100% + 16px)" : "100%"}
          maxWidth={widgetStyle ? "unset" : "100%"}
        >
          <div
            className={classNames(
              styles.thumbnail,
              widgetStyle ? styles.widget : styles.dashboard,
              {
                [styles.small]: small,
                [styles.loading]: isNotReady
              }
            )}
          >
            {hasPreview && dataUrl && renderImage(dataUrl)}
            {!hasPreview && renderFileInfo(shortFileName, file.size)}
            {uploadState === FileUploadState.IN_PROGRESS && (
              <div className={styles["loader-container"]}>
                {(widgetStyle && <L10n id={"conversation.files.uploading"} />) || (
                  <CloserLoaderComponent />
                )}
              </div>
            )}
            {isUploadFailed(uploadState) && renderErrorOverlay(hasPreview)}
            {!widgetStyle && renderFold()}
            {renderRemoveFileButton()}
          </div>
        </TooltipComponent>
      );
    };

    const renderAttachment = (att: IAttachment) => {
      const { token, fileName, size, id, previews } = att;
      const hasPreview = previews && previews.length > 0 && previews[0];
      const shortFileName = getShortFileName(fileName || "");

      const onClick: React.MouseEventHandler<HTMLAnchorElement> = (ev) => {
        if (isIosWebview) {
          if (ev.currentTarget.href === temporaryLink) {
            setTimeout(() => setTemporaryLink(undefined), 100);
            if (onFileDownload) {
              onFileDownload(getWheelhouseDownloadUrl(token), id, fileName);
            }
          } else {
            ev.preventDefault();
          }
        } else {
          ev.preventDefault();
          fileService.getOneTimeLink(getWheelhouseDownloadUrl(token)).then((link) => {
            window.open(link, isIosBrowser ? "_self" : "_blank");
            if (onFileDownload) {
              onFileDownload(getWheelhouseDownloadUrl(token), id, fileName);
            }
          });
        }
      };

      const onHover: React.MouseEventHandler<HTMLAnchorElement> = () => {
        if (isIosWebview) {
          fileService.getOneTimeLink(getWheelhouseDownloadUrl(token)).then(setTemporaryLink);
        }
      };

      return (
        <TooltipComponent
          text={fileName}
          tooltipShow={!!fileName}
          positionPreset={"top"}
          breakWords
          style={widgetStyle ? "widget" : "dashboard"}
          width={widgetStyle ? "calc(100% + 16px)" : "100%"}
          maxWidth={widgetStyle ? "unset" : "100%"}
        >
          <div
            className={classNames(styles.thumbnail, {
              [styles.widget]: widgetStyle,
              [styles.dashboard]: !widgetStyle
            })}
          >
            {hasPreview && renderImage(getWheelhouseDownloadUrl(token, previews[0]!))}
            {!hasPreview && renderFileInfo(shortFileName, size)}
            <div
              className={classNames(styles.overlay, { [styles["semi-transparent"]]: hasPreview })}
            >
              <a
                download={fileName || id}
                href={temporaryLink || getWheelhouseDownloadUrl(token)}
                target="_blank"
                className={classNames(styles.text, styles.button)}
                onClick={onClick}
                onMouseEnter={onHover}
              >
                {
                  (widgetStyle && <i className="icon-download-widget" />) ||
                  <>
                    <i className="icon-download" />
                    {intl.formatMessage("conversation.files.download")}
                  </>
                }
              </a>
            </div>
            {renderFold()}
          </div>
        </TooltipComponent>
      );
    };

    const render = () => {
      if (attachment) {
        return renderAttachment(attachment);
      }
      if (fileUploadInfo) {
        return renderLocalFile(fileUploadInfo);
      }
      return <></>;
    };

    return render();
  }
);
